import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";

const compareProcedures = (p1, p2) => p1.id === p2.id;
const compareProcedureSteps = (s1, s2) => s1.stepid === s2.stepid;

export const procedureSlice = createSlice({
    name: 'procedure',
    initialState: {
        defs: [],
        steps: {}
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        setProcedureSteps: (state, action) => {
            action.payload.forEach(step => {
                delete state.steps[step.procedureid];
            });
            action.payload.forEach(step => {
                if (!state.steps[step.procedureid]) {
                    state.steps[step.procedureid] = [];
                }
                const idx = state.steps[step.procedureid].findIndex(s => compareProcedureSteps(s, step));
                if (idx >= 0) {
                    state.steps[step.procedureid].splice(idx, 1, step);
                } else {
                    state.steps[step.procedureid].push(step);
                }
            });
        },
        addOrUpdateDef: (state, action) => {
            for (const p of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(d => compareProcedures(d, p));
                if (idx >= 0) {
                    state.defs.splice(idx, 1, p)
                } else {
                    state.defs.push(p);
                }
            }
        },
        removeDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(d => compareProcedures(d, def));
                if (idx >= 0) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        addOrUpdateStep: (state, action) => {
            for (const s of ensureArray(action.payload)) {
                if (!state.steps[s.procedureid]) {
                    state.steps[s.procedureid] = [];
                }
                let idx = state.steps[s.procedureid].findIndex(s2 => compareProcedureSteps(s, s2));
                if (idx >= 0) {
                    state.steps[s.procedureid].splice(idx, 1, s);
                } else {
                    state.steps[s.procedureid].push(s);
                }
            }
        },
        removeStep: (state, action) => {
            for (const s of ensureArray(action.payload)) {
                if (state.steps[s.procedureid]) {
                    let idx = state.steps[s.procedureid].findIndex(s2 => compareProcedureSteps(s2, s));
                    if (idx >= 0) {
                        state.steps[s.procedureid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => procedureSlice.getInitialState());
    }
});

// #region network operations
const fetchProcedures = network => network.get('/api/v1/procedures', procedureSlice.actions.setDefs);
export const insertProcedure = (network, procedure) => network.post('/api/v1/procedure', procedure, procedureSlice.actions.addOrUpdateDef);
export const updateProcedure = (network, procedure) => network.put('/api/v1/procedure', procedure, procedureSlice.actions.addOrUpdateDef);
export const removeProcedure = (network, procedure) => network.delete('/api/v1/procedure/' + encodeURIComponent(procedure.id), procedureSlice.actions.removeDef);

const fetchProcedureSteps = procedureid => network => network.get('/api/v1/proceduresteps/' + encodeURIComponent(procedureid), procedureSlice.actions.setProcedureSteps);
export const insertProcedureStep = (network, step) => network.post('/api/v1/procedurestep', step, procedureSlice.actions.addOrUpdateStep);
export const updateProcedureStep = (network, step) => network.put('/api/v1/procedurestep', step, procedureSlice.actions.addOrUpdateStep);
export const deleteProcedureStep = (network, step) => network.delete('/api/v1/procedurestep/' + encodeURIComponent(step.stepid), procedureSlice.actions.removeStep);

// #region selectors
const selectAllProcedures = state => state.procedure.defs;
const selectProcedureForSiteLevelTypeProblemPriority = createSelector([selectAllProcedures, (procedures, site) => site, (procedures, site, level) => level, (procedures, site, level, type) => type, (procedures, site, level, type, problem) => problem, (procedures, site, level, type, problem, priority) => priority], (procedures, site, level, type, problem, priority) => {
    return procedures.filter(procedure => procedure.applications.some(a => (!a.site || a.site === site) && (!a.level || a.level === level) && (!a.type || a.type === type) && (!a.priority || a.priority === priority) && (!a.problem || a.problem.toLowerCase().startsWith(problem?.toLowerCase()))));
});
const selectProcedure = (site, level, type, problem, priority) => state => selectProcedureForSiteLevelTypeProblemPriority(state, site, level, type, problem, priority);

const selectAllSteps = state => state.procedure.steps;
const selectStepsForProcedure = createSelector([selectAllSteps, (steps, procedureid) => procedureid], (steps, procedureid) => steps[procedureid] ?? []);
const selectProcedureSteps = procedureid => state => selectStepsForProcedure(state, procedureid);
// #endregion

// #region listeners
const procedureListen = {
    procedureCreated: (procedure, { dispatch }) => dispatch(procedureSlice.actions.addOrUpdateDef(procedure)),
    procedureUpdated: (procedure, { dispatch }) => dispatch(procedureSlice.actions.addOrUpdateDef(procedure)),
    procedureDeleted: (procedure, { dispatch }) => dispatch(procedureSlice.actions.removeDef(procedure))
}
const procedureStepListen = {
    procedureStepCreated: (step, { dispatch }) => dispatch(procedureSlice.actions.addOrUpdateStep(step)),
    procedureStepUpdated: (step, { dispatch }) => dispatch(procedureSlice.actions.addOrUpdateStep(step)),
    procedureStepDeleted: (step, { dispatch }) => dispatch(procedureSlice.actions.removeStep(step))
}
// #endregion

// #region permission checks
export const canInsertProcedure = (credentials, procedure) => credentials.access.hasFull('administrator');
export const canUpdateProcedure = (credentials, procedure) => credentials.access.hasFull('administrator');
export const canRemoveProcedure = (credentials, procedure) => credentials.access.hasFull('administrator');

export const canInsertStep = (credentials, step) => credentials.access.hasFull('administrator');
export const canUpdateStep = (credentials, step) => credentials.access.hasFull('administrator');
export const canRemoveStep = (credentials, step) => credentials.access.hasFull('administrator');
// #endregion

export const [useAllProcedures] = dataHookFactory(selectAllProcedures, { run: fetchProcedures, listen: procedureListen });
export const [useProcedures] = dataHookFactory(selectProcedure, () => ({ run: fetchProcedures, listen: procedureListen }));
export const [useProcedureSteps] = dataHookFactory(selectProcedureSteps, initMapFactory(fetchProcedureSteps, procedureStepListen));

const reducerDetails = { name: procedureSlice.name, reducer: procedureSlice.reducer };
export default reducerDetails;